export type TaskStatus =
  | 'not_started'
  | 'in_progress'
  | 'submitted'
  | 'approved'
  | 'closed'
  | string;

export interface HubTask {
  id: string;
  process_run_id: string;
  title: string;
  cycle_label: string;
  period: string;
  model_id?: number;
  scenario_id?: number;
  due_at: string | null;
  overdue: boolean;
  dimension_item_id: number;
  dimension_item_name: string;
  status: TaskStatus;
  reviewer_user_id: number | null;
  attention_count: number;
  comment_count: number;
}

export interface SliceCell {
  indicator_or_column: string;
  coordinates: Record<string, string>;
  role: string;
  value: number | string | null;
  display: string | null;
  block_id: number | null;
  /** Time periods for this dimension tuple. Absent when the cell is a single value. */
  periods?: Record<string, number | string | null>;
}

export interface WorkspaceColumn {
  indicator_or_column: string;
  label: string;
  editable: boolean;
}

export interface WorkspaceBlock {
  kind: string;
  text?: string | null;
  block_id?: number | null;
  label?: string | null;
  placeholder?: string | null;
  flag?: string | null;
  columns?: WorkspaceColumn[];
  items?: Array<{
    label: string;
    indicator_or_column?: string | null;
    constant?: number | string | null;
    display?: string | null;
  }>;
  lines?: Array<{ label: string; indicator_or_column: string }>;
  data_table_id?: number | null;
  data_table_columns?: Array<{ name: string; label?: string | null }>;
  scope_filters?: Array<{
    column: string;
    op?: string;
    value_from?: 'dimension_item_name' | 'dimension_item_id';
  }>;
  missing_header?: boolean;
  /** @deprecated Published specs before the data_table rename. */
  data_source_id?: number | null;
  data_source_columns?: Array<{ name: string; label?: string | null }>;
}

export interface WorkspaceTab {
  id: string;
  label: string;
  heading: string;
  guidance?: string | null;
  blocks: WorkspaceBlock[];
}

export interface WorkspaceSpec {
  version: number;
  tabs: WorkspaceTab[];
  submission_rules?: unknown[];
}

export interface TaskComment {
  id: string;
  task_id: string;
  author_user_id: number;
  body: string;
  created_at: string;
}

export interface ActivityEvent {
  id: string;
  event_type: string;
  actor_user_id: number | null;
  created_at: string;
  metadata: Record<string, unknown>;
}

export interface WorkspaceAnswers {
  flags: Record<string, boolean>;
  commentary: string;
  assumptions: string;
}

export interface TaskCapabilities {
  reassign: boolean;
  approve: boolean;
  reopen: boolean;
  close: boolean;
  change_due: boolean;
}

export const emptyCapabilities: TaskCapabilities = {
  reassign: false,
  approve: false,
  reopen: false,
  close: false,
  change_due: false,
};

export interface TaskSummary {
  id: string;
  title: string;
  description: string;
  cycle_label: string;
  period: string;
  due_at: string | null;
  overdue: boolean;
  status: TaskStatus;
  reviewer_user_id?: number | null;
  assignee_user_ids?: number[];
  model_id?: number;
  scenario_id?: number;
  dimension_item_name: string;
  dimension_item_id?: number;
  reopen_note?: string | null;
}

export interface TaskWorkspace {
  task: TaskSummary;
  capabilities?: TaskCapabilities;
  render: {
    mode: 'live' | 'snapshot';
    reason: string;
    editable: boolean;
    submission_id?: string | null;
  };
  workspace_spec: WorkspaceSpec;
  /** Saved flags and commentary. Present on the task itself, before the numbers. */
  workspace_state?: WorkspaceAnswers;
  view?:
    | {
        source: 'live';
        slice: { cells: SliceCell[] };
        workspace_state: WorkspaceAnswers;
      }
    | {
        source: 'snapshot';
        payload: WorkspaceAnswers & { cells: SliceCell[] };
      };
}
